import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon, ChevronDownIcon } from "@heroicons/react/outline";
import Navbar from "../../components/tickets/navBar";
import Footer from "../../components/tickets/footer";
import CookieBanner from "../../components/tickets/cookieBanner";

const faqData = {
  es: [
    {
      section: "Reserva y Compra",
      items: [
        {
          q: "¿Cómo puedo comprar una entrada?",
          a: "Las entradas se lanzan en oleadas programadas, ofreciendo acceso a diferentes categorías a lo largo del tiempo.\n\nSi una categoría aún no está disponible, se mostrará un contador o una fecha de lanzamiento indicando cuándo saldrá a la venta. Le invitamos a volver en ese momento para asegurar su acceso.\n\nTenga en cuenta que, una vez que una categoría se encuentra agotada, no volverá a ponerse a la venta.",
        },
        {
          q: "¿Qué significan los niveles, fases u oleadas?",
          a: "Los niveles (también denominados fases u oleadas) representan distintas etapas de lanzamiento de entradas, no diferentes zonas o áreas dentro del evento.\n\nTodas las entradas dentro de una misma categoría (por ejemplo, General o VIP) ofrecen el mismo nivel de acceso y experiencia, independientemente del nivel en el que se hayan adquirido.\n\nCada nivel dispone de un número limitado de entradas a un precio determinado. A medida que se agotan, se libera el siguiente nivel, generalmente a un precio superior.\n\nEste sistema recompensa a quienes compran con antelación y garantiza una distribución organizada de las entradas.",
        },
        {
          q: "¿Cuáles son las diferentes áreas de acceso (General, VIP, Backstage)?",
          a: "Dependiendo del evento, pueden existir distintos niveles de acceso diseñados para mejorar su experiencia. Tenga en cuenta que no todos los eventos incluyen todas las opciones.\n\n• Acceso General: Permite la entrada a las áreas principales del evento, como escenarios, barras y servicios estándar.\n\n• Acceso VIP: Incluye todos los beneficios del Acceso General, además de acceso a zonas VIP exclusivas, que pueden ofrecer mejores vistas, barras dedicadas y mayor comodidad.\n\n• Acceso Backstage: Permite la entrada a áreas específicas detrás del escenario, ofreciendo una experiencia más privada y exclusiva.\n\nTenga en cuenta que el acceso backstage no incluye entrada a la cabina del DJ ni interacción directa con los artistas.\n\nTodas las opciones disponibles se detallan claramente en la página de cada evento.",
        },
        {
          q: "¿Necesito una cuenta para comprar entradas?",
          a: "Puede completar su compra como invitado. Sin embargo, crear una cuenta le permitirá gestionar sus reservas, acceder a sus entradas en cualquier momento y recibir actualizaciones importantes.\n\nPara una experiencia más fluida y personalizada, recomendamos crear una cuenta.",
        },
      ],
    },
    {
      section: "Pagos",
      items: [
        {
          q: "¿Qué métodos de pago aceptan?",
          a: "Aceptamos las principales tarjetas de crédito y débito, así como otros métodos de pago locales según su región. Las opciones disponibles se mostrarán en el proceso de compra.",
        },
        {
          q: "¿Es seguro el pago?",
          a: "Sí. Todas las transacciones se procesan de forma segura a través de Stripe, un proveedor de pagos reconocido a nivel global.\n\nSus datos están protegidos mediante cifrado estándar de la industria y autenticación 3D Secure (3DS), que añade una capa adicional de seguridad cuando es necesario.\n\nNo almacenamos datos de tarjetas ni información sensible de pago en nuestra plataforma.",
        },
        {
          q: "¿Por qué se rechazó mi pago?",
          a: "Los pagos pueden ser rechazados por diversos motivos, como fondos insuficientes, restricciones bancarias o datos incorrectos.\n\nSi su pago no se procesa correctamente, le recomendamos verificar la información e intentarlo nuevamente, o contactar con su banco.",
        },
      ],
    },
    {
      section: "Entradas y Acceso",
      items: [
        {
          q: "¿Cómo recibiré mi entrada?",
          a: "Su entrada se enviará por correo electrónico y también estará disponible en su panel de usuario poco después de completar la compra.",
        },
        {
          q: "¿Necesito imprimir mi entrada?",
          a: "No, las entradas digitales son válidas salvo indicación contraria. Puede presentarla directamente desde su dispositivo móvil en el acceso al evento.",
        },
        {
          q: "¿Qué hago si no recibo mi entrada?",
          a: "Le recomendamos revisar primero su carpeta de spam o correo no deseado. Si no la encuentra, contacte con nuestro equipo de soporte indicando los datos de su pedido, y le ayudaremos a la mayor brevedad.",
        },
      ],
    },
    {
      section: "Cambios, Cancelaciones y Reembolsos",
      items: [
        {
          q: "¿Puedo cancelar mi entrada?",
          a: "Por lo general, las entradas no pueden cancelarse una vez adquiridas, ya que las políticas dependen del organizador del evento.\n\nSi no puede asistir, le recomendamos utilizar TicketSwap, nuestra plataforma asociada de reventa, donde podrá ofrecer su entrada de forma segura a otros usuarios.\n\nLe aconsejamos revisar siempre los términos y condiciones específicos del evento antes de realizar la compra.",
        },
        {
          q: "¿Puedo modificar mi reserva?",
          a: "Las modificaciones normalmente no están permitidas, salvo que el organizador lo indique expresamente.\n\nEn caso de estar disponibles, las opciones de cambio aparecerán en su cuenta.",
        },
        {
          q: "¿Cómo funcionan los reembolsos?",
          a: "Los reembolsos se realizan únicamente conforme a la política del organizador del evento y, por lo general, no están disponibles una vez realizada la compra.\n\nEn caso de que se autorice un reembolso (por ejemplo, por cancelación o cambios significativos en el evento), solo se reembolsará el valor de la entrada. Las comisiones de servicio y plataforma no son reembolsables.\n\nLos tiempos de procesamiento pueden variar y tardar varios días laborables.",
        },
      ],
    },
    {
      section: "Información del Evento",
      items: [
        {
          q: "¿Qué ocurre si un evento se cancela o se pospone?",
          a: "En caso de cancelación o aplazamiento, será notificado por correo electrónico con toda la información relevante.\n\nDependiendo de la situación y de la política del organizador, podrá disponer de opciones como reprogramación o reembolso.",
        },
        {
          q: "¿Existen restricciones de edad?",
          a: "Algunos eventos pueden tener restricciones de edad o requisitos de acceso.\n\nLe recomendamos revisar detenidamente la página del evento antes de comprar para asegurarse de cumplir con todas las condiciones.",
        },
      ],
    },
  ],
  en: [
    {
      section: "Booking & Purchasing",
      items: [
        {
          q: "How do I buy a ticket?",
          a: "Tickets are released in scheduled waves, granting access to different categories over time.\n\nIf a ticket category is not yet available, a countdown timer or release date will indicate when sales will begin. We invite you to return at that time to secure your place.\n\nPlease note that once a ticket category is sold out, it will not be released again.",
        },
        {
          q: "What do tiers, phases, or waves mean?",
          a: "Tiers (also referred to as phases or waves) indicate different stages of ticket release, not different areas or access levels within the event.\n\nAll tickets within the same category (such as General Access or VIP) provide the same experience and privileges, regardless of the tier in which they were purchased.\n\nEach tier offers a limited number of tickets at a set price. As availability is exhausted, the next tier is released — typically at a higher rate.\n\nThis approach rewards early purchasers while ensuring a structured and seamless ticket release.",
        },
        {
          q: "What are the different access areas (General, VIP, Backstage)?",
          a: "Depending on the event, multiple access levels may be available to enhance your experience. Please note that not all events include every access type.\n\n• General Access: Entry to the main event areas, including stages, bars, and standard facilities.\n\n• VIP Access: Includes all General Access privileges, along with entry to exclusive VIP areas, which may feature premium viewing zones, dedicated bars, and elevated comfort.\n\n• Backstage Access: Provides entry to designated behind-the-scenes areas, offering a more private and curated environment.\n\nPlease note that backstage access does not include entry to the DJ booth or unrestricted interaction with performers.\n\nAll available access options are clearly outlined on each event page.",
        },
        {
          q: "Do I need an account to book tickets?",
          a: "You may complete your purchase as a guest. However, creating an account allows you to manage your bookings, access your tickets at any time, and receive important event updates.\n\nFor a more seamless and personalized experience, we recommend creating an account.",
        },
      ],
    },
    {
      section: "Payments",
      items: [
        {
          q: "What payment methods do you accept?",
          a: "We accept major credit and debit cards, along with additional local payment methods depending on your region. Available options will be presented at checkout.",
        },
        {
          q: "Is my payment secure?",
          a: "Yes. All transactions are securely processed via Stripe, a globally trusted payment provider.\n\nYour payment information is protected using industry-standard encryption and 3D Secure (3DS) authentication, providing an additional layer of protection where required.\n\nWe do not store any card details or sensitive payment information on our platform.",
        },
        {
          q: "Why was my payment declined?",
          a: "Payments may be declined due to insufficient funds, bank restrictions, or incorrect card details.\n\nIf your transaction is unsuccessful, please review your information and try again, or contact your bank for further assistance.",
        },
      ],
    },
    {
      section: "Tickets & Access",
      items: [
        {
          q: "How will I receive my ticket?",
          a: "Your ticket will be delivered via email and made available in your account dashboard shortly after purchase.",
        },
        {
          q: "Do I need to print my ticket?",
          a: "No, digital tickets are accepted unless otherwise specified. You may present your ticket directly from your mobile device upon entry.",
        },
        {
          q: "What if I don't receive my ticket?",
          a: "Please check your spam or junk folder first. If you are still unable to locate your ticket, contact our support team with your order details, and we will assist you promptly.",
        },
      ],
    },
    {
      section: "Changes, Cancellations & Refunds",
      items: [
        {
          q: "Can I cancel my ticket?",
          a: "Tickets are generally non-cancellable once purchased, as policies are determined by the event organizer.\n\nIf you are unable to attend, we recommend using TicketSwap, our trusted resale partner, where you may securely offer your ticket to other attendees.\n\nPlease review the event-specific terms and conditions prior to purchase.",
        },
        {
          q: "Can I change my booking?",
          a: "Booking modifications are typically not permitted, unless explicitly stated by the event organizer.\n\nWhere changes are allowed, the option will be available in your account dashboard.",
        },
        {
          q: "How do refunds work?",
          a: "Refunds are issued only in accordance with the event organizer's policy and are not typically available after purchase.\n\nIn cases where a refund is approved (such as event cancellation or significant changes), only the ticket value will be refunded. Platform and service fees are non-refundable.\n\nProcessing times may vary and can take several business days.",
        },
      ],
    },
    {
      section: "Event Information",
      items: [
        {
          q: "What happens if an event is cancelled or postponed?",
          a: "In the event of a cancellation or postponement, you will be notified promptly via email with full details.\n\nDepending on the circumstances and the organizer's policy, you may be offered options such as rescheduling or a refund.",
        },
        {
          q: "Are there age restrictions?",
          a: "Certain events may be subject to age restrictions or entry requirements.\n\nWe recommend reviewing the event details page carefully prior to purchase to ensure compliance with all applicable conditions.",
        },
      ],
    },
  ],
};

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-green-900/30 rounded-xl overflow-hidden transition-all">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-green-900/10 transition"
      >
        <p className="text-sm font-medium text-white pr-4">{q}</p>
        <ChevronDownIcon
          className={`w-4 h-4 text-green-400/70 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-green-900/20">
          <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-line pt-4">
            {a}
          </p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const navigate = useNavigate();
  const [lang, setLang] = useState("es");
  const data = faqData[lang];

  return (
    <div className="bg-[#021610] text-white min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 max-w-3xl mx-auto px-4 pt-24 pb-16 w-full">
        {/* BACK */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-green-400 hover:text-green-300 transition mb-8"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {lang === "es" ? "Volver" : "Back"}
        </button>

        {/* HEADER */}
        <div className="flex items-start justify-between gap-4 mb-10">
          <div>
            <p className="text-[10px] tracking-widest text-green-400/70 uppercase mb-2">
              {lang === "es" ? "Ayuda" : "Help"}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold">
              {lang === "es" ? "Preguntas Frecuentes" : "Frequently Asked Questions"}
            </h1>
          </div>

          {/* LANGUAGE SWITCHER */}
          <div className="flex bg-[#031c14] border border-green-900/30 rounded-xl p-1 flex-shrink-0 mt-1">
            <button
              onClick={() => setLang("es")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium tracking-widest uppercase transition
                ${lang === "es" ? "bg-green-500 text-black" : "text-gray-400 hover:text-white"}`}
            >
              ES
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium tracking-widest uppercase transition
                ${lang === "en" ? "bg-green-500 text-black" : "text-gray-400 hover:text-white"}`}
            >
              EN
            </button>
          </div>
        </div>

        {/* SECTIONS */}
        <div className="space-y-10">
          {data.map((section) => (
            <div key={section.section}>
              <p className="text-[10px] tracking-widest text-green-400/70 uppercase mb-4">
                {section.section}
              </p>
              <div className="space-y-3">
                {section.items.map((item) => (
                  <FAQItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CONTACT */}
        <div className="mt-12 bg-[#031c14] border border-green-900/30 rounded-2xl p-6 text-center">
          <p className="text-sm text-gray-400 leading-relaxed">
            {lang === "es"
              ? "¿No encuentras lo que buscas? Contáctanos en "
              : "Can't find what you're looking for? Contact us at "}
            <a
              href="mailto:contact@afrodisemusic.com"
              className="text-green-400 hover:text-green-300 transition"
            >
              contact@afrodisemusic.eu
            </a>
          </p>
        </div>
      </div>

      <Footer />
      <CookieBanner />
    </div>
  );
}